<template>
    <v-alert
        v-if="showAlert"
        :type="alertType"
        class="mx-auto text-center"
        style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
        elevation="2"
        prominent
    >
        {{ alertMessage }}
    </v-alert>
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row>
                <v-col class="headline mb-4">
                    <h2>Stock almacen</h2>
                </v-col>
                <v-col cols="12" sm="6" style="text-align: right">
                    <v-btn color="primary" :loading="isImporting" :disabled="isImporting" @click="startStocks" v-if="!isWarehouse">
                        <template #loader>
                            <v-progress-circular indeterminate size="20" color="white" />
                        </template>
                        Generar stocks
                    </v-btn>
                    <v-btn style="margin-left: 10px"  @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <v-row class="mb-4" dense>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="filters.description" label="Filtrar por Stock" dense clearable />
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="filters.client" label="Filtrar por Cliente" dense clearable />
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="filters.campaign" label="Filtrar por Campaña" dense clearable />
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="filters.references" label="Filtrar por Referencia" dense clearable />
                </v-col>
            </v-row>

            <v-data-table
                :items="filteredStocks"
                :headers="headers"
                class="elevation-1"
                item-value="id"
                no-data-text="No hay lineas de stock disponibles"
            >
                <template #item="{ item }">
                    <tr :class="rowClass(item)">
                        <td>{{ item.description }}</td>
                        <td>{{ item.client }}</td>
                        <td>{{ item.campaign }}</td>
                        <td>{{ item.start_date }}</td>
                        <td>{{ item.quantity_pendiente }}</td>
                        <td>{{ item.quantity_in }}</td>
                        <td>{{ item.status }}</td>
                        <td>{{ item.notes }}</td>
                        <td>{{ item.references }}</td>
                        <td style="min-width: 260px">
                            <v-btn
                                icon
                                class="ma-1"
                                title="Validar"
                                @click="onValidate(item)"
                                :disabled="item.quantity_restante === 0"
                            >
                                <v-icon>mdi-check</v-icon>
                            </v-btn>
                            <v-btn class="ma-1" icon title="Moviment" @click="onMove(item)">
                                <v-icon>mdi-swap-horizontal</v-icon>
                            </v-btn>
                            <v-btn class="ma-1" icon title="Notes" @click="onNotes(item)">
                                <v-icon>mdi-note-text</v-icon>
                            </v-btn>
                            <v-btn class="ma-1" v-if="!isWarehouse" icon title="Historial" @click="onHistory(item)">
                                <v-icon>mdi-history</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </template>
            </v-data-table>

        </v-card>

        <v-dialog v-model="movementDialog" max-width="400">
            <v-card>
                <v-card-title>Movimiento de stock</v-card-title>
                <v-card-text>
                    <v-radio-group v-if="!isWarehouse" v-model="movementType" row>
                        <v-radio label="Salida de almacen" value="add"></v-radio>
                        <v-radio label="Entrada a almacen" value="subtract"></v-radio>
                    </v-radio-group>

                    <v-text-field
                        v-model="movementQuantity"
                        label="Cantidad"
                        type="number"
                        min="1"
                    ></v-text-field>

                    <p v-if="movementError" style="color: red">{{ movementError }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="movementDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="applyMovement">Aplicar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="notesDialog" max-width="500">
            <v-card>
                <v-card-title>Añadir notas</v-card-title>
                <v-card-text>
                    <v-textarea
                        v-model="noteText"
                        label="Notes"
                        rows="4"
                        auto-grow
                    ></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="notesDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="saveNotes">Guardar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const isWarehouse = inject('isWarehouse')

const router = useRouter()

const isImporting = ref(false)

import { useAlert } from '@/composables/useAlert'

const {
    alertMessage,
    alertType,
    showAlert,
    showAlertMessage
} = useAlert()


const stocks = ref([])

const headers = [
    { title: 'Stock', value: 'description', sortable: true },
    { title: 'Cliente', value: 'client', sortable: true },
    { title: 'Campaña', value: 'campaign', sortable: true },
    { title: 'Inicio de Campaña', value: 'start_date', sortable: true },
    { title: 'Cantidad por llegar', value: 'quantity_pendiente', sortable: true },
    { title: 'Cantidad en almacen', value: 'quantity_in', sortable: true },
    { title: 'Estado', value: 'status', sortable: true },
    { title: 'Notas', value: 'notes', sortable: true },
    { title: 'Referencias', value: 'references', sortable: true },
    { title: 'Acciones', value: 'actions', sortable: false }
]

// Filtres
const filters = ref({
    description: '',
    client: '',
    campaign: '',
})

const filteredStocks = computed(() => {
    return stocks.value.filter((stock) => {
        return (
            (!filters.value.description || lower(stock?.description).includes(lower(filters.value.description))) &&
            (!filters.value.client || lower(stock?.client).includes(lower(filters.value.client))) &&
            (!filters.value.campaign || lower(stock?.campaign).includes(lower(filters.value.campaign)))&&
            (!filters.value.references || lower(stock?.references).includes(lower(filters.value.references)))
        )
    })
})

const lower = (v) => (v ?? '').toString().toLowerCase()
const normalizeActive = (val) => {
    if (typeof val === 'boolean') return val
    if (typeof val === 'number') return val === 1
    if (typeof val === 'string') return val === '1' || val.toLowerCase() === 'true'
    return false
}

const movementDialog = ref(false)
const movementStock = ref(null)
const movementType = ref('subtract')
const movementQuantity = ref(0)
const movementError = ref('')

const onMove = (item) => {
    movementStock.value = item
    movementType.value = 'subtract'
    movementQuantity.value = 0
    movementError.value = ''
    movementDialog.value = true
}

const notesDialog = ref(false)
const notesStock = ref(null)
const noteText = ref('')

const onNotes = (item) => {
    notesStock.value = item
    noteText.value = item.notes || ''
    notesDialog.value = true
}

const fetchStocks = async () => {
    try {

        const { data } = await axios.get(`/api/admin/stocks/`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })

        stocks.value = data

    } catch (e) {
        showAlertMessage('Error al cargar líneas de stocks', 'error')
        console.error('Error al cargar líneas de stocks:', e)
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
    }
}

const startStocks = async () => {
    isImporting.value = true
    showAlertMessage('Generando stocks...', 'success')
    try {
        await axios.post('/api/admin/stocks/import', {}, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        await fetchStocks()
        showAlertMessage('Stocks generados correctamente.', 'success')
    } catch (e) {
        console.error('Error al importar stocks:', e)
        showAlertMessage('Error al importar stocks', 'error')
    } finally {
        isImporting.value = false
    }
}

const onValidate = async (item) => {
    try {
        await axios.post(`/api/admin/stocks/${item.id}/validate`, {}, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })

        await fetchStocks()
    } catch (error) {
        console.error('Error al validar el stock:', error)
        showAlertMessage('Error al validar el stock', 'error')
    }
}

const applyMovement = async () => {
    movementError.value = ''
    const quantity = Number(movementQuantity.value)

    if (!quantity || quantity <= 0) {
        movementError.value = 'La cantidad ha de ser un número positivo.'
        return
    }

    if (movementType.value === 'subtract' && quantity > movementStock.value.quantity) {
        movementError.value = 'No puedes restar mas cantidad que la disponible.'
        return
    }

    try {
        await axios.post(`/api/admin/stocks/${movementStock.value.id}/move`, {
            type: movementType.value,
            quantity
        }, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })

        movementDialog.value = false
        await fetchStocks()
    } catch (e) {
        showAlertMessage('Error al aplicar movimiento.', 'error')
    }
}

const saveNotes = async () => {
    try {
        await axios.post(`/api/admin/stocks/${notesStock.value.id}/notes`, {
            notes: noteText.value
        }, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })

        notesDialog.value = false
        await fetchStocks()
    } catch (e) {
        showAlertMessage('Error al guardar les notes.', 'error')
        console.error(e)
    }
}

const onHistory = (item) => {
    router.push(`stocks/history/${encodeURIComponent(item.description)}`)
}

// Converteix 'YYYY-MM-DD' o 'YYYY-MM-DD HH:mm:ss' a Date segur per Safari
const toDate = (s) => {
    if (!s) return null
    const norm = s.includes('T') ? s : s.replace(' ', 'T')
    const d = new Date(norm)
    return isNaN(d.getTime()) ? null : d
}

const hoursUntil = (date) => (date - new Date()) / 36e5 // mil·li → hores

const rowClass = (item) => {
    const pending = Number(item?.quantity_pendiente ?? 0) > 0
    const start = toDate(item?.start_date)

    if (!pending || !start) return ''

    const h = hoursUntil(start)

    // Ja ha començat o ≤24h → vermell
    if (h <= 24) return 'danger-row'

    // Entre 24h i 48h → groc
    if (h <= 48) return 'warning-row'

    return ''
}

onMounted(fetchStocks)
</script>

<style scoped>
.expired-row {
    background-color: #ffebee !important; /* vermell suau */
}
.warning-row {
    background-color: #fff8e1 !important; /* groc suau (amber lighten-4) */
}
.danger-row {
    background-color: #ffebee !important; /* vermell suau */
}
</style>
